import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./login.css";

interface LoginProps {
  onLoginSuccess: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState<string>(""); // For login
  const [password, setPassword] = useState<string>(""); // For login
  const [email, setEmail] = useState<string>(""); // For forgot password and reset password
  const [resetCode, setResetCode] = useState<string>(""); // Reset code for password reset
  const [newPassword, setNewPassword] = useState<string>(""); // New password for reset
  const [error, setError] = useState<string | null>(null); // Error messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null); // Success messages
  const [currentStep, setCurrentStep] = useState<"login" | "forgotPassword" | "resetPassword">("login"); // Step tracking
  const [loading, setLoading] = useState<boolean>(false); // Loading state
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

  // Axios instance for API requests
  const axiosInstance = axios.create({
    baseURL: BACKEND_URL,
    headers: {
      "ngrok-skip-browser-warning": "true", // Bypass ngrok warning
    },
  });

  // Handle form submission based on the current step
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (currentStep === "forgotPassword") {
        // Forgot password flow
        const response = await axiosInstance.post(`/api/forgot-password`, { email });
        setSuccessMessage(response.data.message || "A reset code has been sent to your email.");
        setCurrentStep("resetPassword"); // Move to reset password step
      } else if (currentStep === "resetPassword") {
        // Reset password flow
        const response = await axiosInstance.post(`/api/reset-password`, {
          email,
          loginCode: resetCode,
          newPassword,
        });
        setSuccessMessage(response.data.message || "Password reset successfully. Please log in.");
        setTimeout(() => setCurrentStep("login"), 3000); // Redirect to login after success
      } else {
        // Login flow
        const response = await axiosInstance.post(`/api/login`, { username, password });
        localStorage.setItem("token", response.data.token); // Save the token
        onLoginSuccess();
        navigate("/"); // Redirect to dashboard
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordToggle = () => {
    setCurrentStep(currentStep === "login" ? "forgotPassword" : "login");
    setError(null);
    setSuccessMessage(null);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return (
    <div className="login-container">
      <form onSubmit={handleSubmit} className="login-form">
        {currentStep === "login" && (
          <>
            <h2 className="login-header">Login</h2>
            {error && <p className="error-message">{error}</p>}
            {successMessage && <p className="success-message">{successMessage}</p>}
            <div className="input-container">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="login-input"
                required
              />
            </div>
            <div className="input-container">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="login-input"
                required
              />
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </button>
            <button type="button" onClick={handleForgotPasswordToggle} className="link-button">
              Forgot Password?
            </button>
          </>
        )}

        {currentStep === "forgotPassword" && (
          <>
            <h2 className="login-header">Forgot Password</h2>
            {error && <p className="error-message">{error}</p>}
            {successMessage && <p className="success-message">{successMessage}</p>}
            <div className="input-container">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="login-input"
                required
              />
            </div>
            <button
              type="submit"
              className="login-button"
              disabled={loading || !validateEmail(email)}
            >
              {loading ? "Sending Reset Code..." : "Send Password"}
            </button>
            <button type="button" onClick={handleForgotPasswordToggle} className="link-button">
              Back to Login
            </button>
          </>
        )}

        {currentStep === "resetPassword" && (
          <>
            <h2 className="login-header">Reset Password</h2>
            {error && <p className="error-message">{error}</p>}
            {successMessage && <p className="success-message">{successMessage}</p>}
            <div className="input-container">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="login-input"
                required
              />
            </div>
            <div className="input-container">
              <label htmlFor="resetCode">Reset Code</label>
              <input
                id="resetCode"
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                className="login-input"
                required
              />
            </div>
            <div className="input-container">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="login-input"
                required
              />
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? "Resetting Password..." : "Reset Password"}
            </button>
            <button type="button" onClick={() => setCurrentStep("login")} className="link-button">
              Back to Login
            </button>
          </>
        )}
      </form>
    </div>
  );
};

export default Login;
